import { variables } from '../theme';

export const NOTIFICATION_TYPES = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

/**
 * Step numbering is shared with `users.onboarding.onboardingStep` on the server.
 *
 * It starts at 2 rather than 1 because the account step used to sit at 1: the
 * questionnaire now runs before registration, but keeping the questions on the
 * numbers they have always had means accounts created under the old order
 * resume exactly where they left off, with no migration.
 *
 * `finalStep` is a single slot with two faces — a visitor who has answered the
 * questionnaire is asked to create an account there, and once they have one the
 * same slot shows the plan choice.
 */
export const ONBOARDING_CONFIG = {
  totalSteps: 8,
  firstStep: 2,
  finalStep: 8,
};

export const ROWS_PER_PAGE = 25;
export const POST_ROWS_PER_PAGE = 10;

export const USER_DEFAULTS = {
  height: 170,
  weight: 70,
  bodyComposition: 'average',
};

export const USER_ROLES = {
  admin: 'ADMIN',
  invited: 'INVITED',
  user: 'USER',
};

/**
 * Check if user is an admin (can access admin sections)
 */
export const isAdmin = (role) => role === USER_ROLES.admin;

/**
 * Check if user bypasses subscription/plan requirements for features
 * - ADMIN: Full feature access (they're admins)
 * - INVITED: Full feature access (granted by admin)
 * - USER: Feature access based on their subscription plan
 */
export const bypassesPlanRestrictions = (role) => {
  return role === USER_ROLES.admin || role === USER_ROLES.invited;
};

export const FASTING_STAGES = [
  {
    value: 0,
    color: variables.colors.salmon,
    label: 'Fed',
    description: 'Digestion phase, nutrient absorption',
    longDescription:
      'During the fed stage, your body digests and absorbs nutrients from your last meal. Insulin levels are elevated to help cells take up glucose for energy. This phase typically lasts 3-4 hours after eating. Excess glucose is stored as glycogen in the liver and muscles, while any surplus is converted into fat for long-term storage.',
  },
  {
    value: 14400,
    color: variables.colors.mainBlue,
    label: 'Early Fasting',
    description: 'Glycogen depletion, insulin drops',
    longDescription:
      'In the early fasting stage, digestion is complete and blood insulin levels begin to drop. Your body starts breaking down glycogen stored in the liver for energy. Fat breakdown increases slightly as the body transitions from using dietary glucose to relying on glycogen reserves. This phase can last several hours, depending on activity levels and the size of your last meal.',
  },
  {
    value: 43200,
    color: variables.colors.mainOrange,
    label: 'Fat Burning',
    description: 'Lipolysis begins, fat mobilization',
    longDescription:
      'In the fat burning stage, glycogen stores become depleted, and your body initiates gluconeogenesis, converting amino acids and lactate into glucose. Your body also starts mobilizing fat stores for energy, increasing the release of free fatty acids into the bloodstream. This stage marks the beginning of a metabolic shift towards fat utilization as the main energy source.',
  },
  {
    value: 64800,
    color: variables.colors.fernFrond,
    label: 'Ketosis',
    description: 'Ketone production, enhanced fat burning',
    longDescription:
      'Ketosis begins when the liver converts fatty acids into ketones, which serve as an alternative energy source. As blood glucose levels drop, ketone production increases, leading to greater fat oxidation. Your body enters a state of efficient fat burning, utilizing ketones for energy in the brain and muscles. This stage is associated with enhanced mental clarity and increased fat loss.',
  },
  {
    value: 172800,
    color: variables.colors.purple,
    label: 'Autophagy',
    description: 'Cellular cleanup, tissue repair',
    longDescription:
      'During autophagy, your body reaches an optimal level of ketone production and triggers cellular self-cleaning. Damaged cells and proteins are recycled and repaired. This phase is associated with improved cellular health, reduced inflammation, and potential longevity benefits. Ketone levels are at their highest, providing steady energy while promoting fat loss and preserving muscle.',
  },
  {
    value: 259200,
    color: variables.colors.redBerry,
    label: 'Extended Fast',
    description: 'Deep healing, immune renewal',
    longDescription:
      'Extended fasting goes beyond 72 hours, leading to significant metabolic and healing changes. Your body shifts to conserving protein while primarily using fat stores for energy. Growth hormone levels increase to preserve lean muscle mass, and the immune system may begin regenerating. This phase can provide deep healing benefits but requires careful monitoring and should only be done under medical supervision.',
  },
];

export const INGREDIENTS_IMG_BASE_URL = 'https://img.spoonacular.com/ingredients_250x250/';

export const SUPPORT_EMAIL = 'support@gotowertech.com';

export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.activeshape.app';
